import React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import { useQuery } from "@tanstack/react-query";
import { getFeedZipCodes } from "@/services/UserFeedService";

interface ZipCodeInputProps {
    zipCodes: string[];
    onZipCodesChange: (zipCodes: string[]) => void;
}

const ZipCodeInput = ({ zipCodes, onZipCodesChange }: ZipCodeInputProps) => {
    const [zipInput, setZipInput] = React.useState("");
    const [error, setError] = React.useState(false);

    const { data: zipCodeOptions = [] } = useQuery({
        queryKey: ["feedZipCodes"],
        queryFn: getFeedZipCodes,
        select: (data) => {
            if (data && Array.isArray(data)) {
                return data;
            }
            // Fallback to empty array
            return [];
        },
    });

    const validateZipCode = (zip: string) => {
        // Require a 5-digit numeric zip code
        return /^\d{5}$/.test(zip.trim());
    };

    const handleZipAdd = (event: any, value: string[]) => {
        // Validate zip codes and collect invalid ones
        const invalidZips = value.filter((zip) => !validateZipCode(zip));
        if (invalidZips.length > 0) {
            setError(true);
        } else {
            setError(false);
        }
        // Filter out invalid zip codes
        const validZips = value.filter((zip) => validateZipCode(zip));

        // Remove duplicates
        const uniqueZips = validZips.filter((zip, index) => validZips.indexOf(zip) === index);

        onZipCodesChange(uniqueZips);
        setZipInput("");
    };

    const handleZipDelete = (zip: string) => {
        onZipCodesChange(zipCodes.filter((z) => z !== zip));
    };

    // Reset both selected values and input value
    React.useEffect(() => {
        if (!zipCodes || zipCodes.length === 0) {
            setZipInput("");
            setError(false);
        }
    }, [zipCodes]);

    return (
        <div>
            <label className="tw-block tw-mb-1 tw-text-[14px] tw-text-[#555E67]">Zip Code</label>
            <Autocomplete
                multiple
                freeSolo
                options={zipCodeOptions}
                value={zipCodes || []}
                onChange={handleZipAdd}
                inputValue={zipInput}
                onInputChange={(_, newInputValue) => {
                    // Only allow numbers
                    const numericValue = newInputValue.replace(/[^0-9]/g, "").slice(0, 5);
                    setZipInput(numericValue);
                }}
                onBlur={() => {
                    // If the input has a non-empty zip code not already in the list, add it on blur
                    if (zipInput && validateZipCode(zipInput) && !zipCodes.includes(zipInput)) {
                        onZipCodesChange([...zipCodes, zipInput]);
                        setZipInput("");
                        setError(false);
                    } else if (!validateZipCode(zipInput)) {
                        setError(true);
                    }
                }}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            label={option}
                            {...getTagProps({ index })}
                            onDelete={() => handleZipDelete(option)}
                            sx={{
                                background: "#FFE3D1",
                                color: "#E26A2C",
                                fontWeight: 500,
                                fontSize: 14,
                            }}
                        />
                    ))
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Type or select zip code"
                        size="small"
                        error={error}
                        inputProps={{
                            ...params.inputProps,
                        }}
                        InputProps={{ ...params.InputProps, sx: { borderRadius: 2 } }}
                    />
                )}
            />
        </div>
    );
};

export default ZipCodeInput;
